import {useState, useEffect} from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {colors} from 'shells/colors/colors';
import {Illustration} from 'shells/illustrations/illustration';
import {ImageWrapper} from 'shells/onboarding-modal/image-wrapper';
import {OnboardingModal} from 'shells/onboarding-modal/onboarding-modal';
import {OnboardingBody} from 'shells/onboarding-page/onboarding-body';

import {useDismissibleUserSetting} from '../../../util/hooks';

import {OpenableModals, useOpenableModal} from '../../openable-modals';

import styles from './peep-redesign-onboarding-modal.module.css';

const STATUS_TEXT = 'New: People and Company pages';

interface Props {
    manualOpening?: boolean;
}

export function PeepRedesignOnboardingModal(props: Props) {
    const {manualOpening = false} = props;
    const {onClose: closeOpenableModal} = useOpenableModal(
        OpenableModals.PEEP_REDESIGN_ONBOARDING_MODAL
    );

    const [isManuallyOpened, setIsManuallyOpened] = useState<boolean>(false);
    const [isLocallyHidden, setIsLocallyHidden] = useState<boolean>(false);

    const {shouldShow, onDismiss} = useDismissibleUserSetting(
        Nut_Settings_User.SETTING_HAS_DISMISSED_PEEP_PAGES_REDESIGN_ONBOARDING
    );

    useEffect(() => {
        if (manualOpening) {
            setIsManuallyOpened(true);
            setIsLocallyHidden(false);
        }
    }, [manualOpening]);

    const handleOnboardingModalClose = () => {
        setIsLocallyHidden(true);

        if (isManuallyOpened) {
            setIsManuallyOpened(false);
        }

        if (manualOpening) {
            closeOpenableModal();
        }

        if (shouldShow && onDismiss) {
            void onDismiss();
        }
    };

    const shouldShowAutomatic = shouldShow && NutClientConfig.hasPeepPagesRedesignFF;

    const pages = [
        {
            id: 'peep-redesign-slide-one',
            body: <PeepRedesignSlideOne />,
            art: {
                background: colors.blueXlt,
                image: () => (
                    <ImageWrapper
                        image={
                            <Illustration src='onboarding/peep-redesign/peep-redesign-widgets' />
                        }
                        width={308}
                    />
                ),
            },
        },
        {
            id: 'peep-redesign-slide-two',
            body: <PeepRedesignSlideTwo />,
            art: {
                background: colors.blueXlt,
                image: () => (
                    <ImageWrapper
                        image={
                            <Illustration src='onboarding/peep-redesign/peep-redesign-customize' />
                        }
                        width={270}
                    />
                ),
            },
        },
        {
            id: 'peep-redesign-slide-three',
            body: <PeepRedesignSlideThree />,
            art: {
                background: colors.blueXlt,
                image: () => (
                    <ImageWrapper
                        image={
                            <Illustration
                                src='onboarding/peep-redesign/peep-redesign-workspace'
                                width={325}
                            />
                        }
                        width={325}
                    />
                ),
            },
        },
        {
            id: 'peep-redesign-slide-four',
            body: <PeepRedesignSlideFour />,
            art: {
                background: colors.blueXlt,
                image: () => (
                    <ImageWrapper
                        image={
                            <div className={styles['sidebar-graphic']}>
                                <Illustration
                                    src='onboarding/peep-redesign/peep-redesign-former'
                                    width={299}
                                />
                            </div>
                        }
                        width={299}
                    />
                ),
            },
        },
        {
            id: 'peep-redesign-slide-five',
            body: <PeepRedesignSlideFive />,
            art: {
                background: colors.blueXlt,
                image: () => (
                    <ImageWrapper
                        image={
                            <Illustration
                                src='onboarding/peep-redesign/peep-redesign-swapper'
                                width={401}
                            />
                        }
                        width={401}
                    />
                ),
            },
        },
    ];

    return (
        <OnboardingModal
            trapFocus={false}
            lastButtonText='Done'
            pages={pages}
            onClose={handleOnboardingModalClose}
            onFinish={handleOnboardingModalClose}
            canClickOutsideToClose={false}
            isOpen={!isLocallyHidden && (isManuallyOpened || shouldShowAutomatic)}
            maxHeight={586}
        />
    );
}

function PeepRedesignSlideOne() {
    return (
        <OnboardingBody title='Consistent structure, optimized workflows' statusText={STATUS_TEXT}>
            <p>
                People and Company pages now share their structure with lead pages, allowing you to
                customize the layout, collapse widgets, and easily access key actions for an
                optimized experience.
            </p>
        </OnboardingBody>
    );
}

function PeepRedesignSlideTwo() {
    return (
        <OnboardingBody title='Layout customization' statusText={STATUS_TEXT}>
            <p>
                You can mix and match widgets to customize both people and company pages, making
                your workflow in Nutshell smoother and more efficient.
            </p>
        </OnboardingBody>
    );
}

function PeepRedesignSlideThree() {
    return (
        <OnboardingBody title='Workspace and sidebar widgets' statusText={STATUS_TEXT}>
            <p>
                A new workspace unifies tasks, activities, emails, timeline, and related contacts,
                while the sidebar evolves into collapsible widgets for contact information, leads,
                company hierarchy, and more.
            </p>
        </OnboardingBody>
    );
}

function PeepRedesignSlideFour() {
    return (
        <OnboardingBody title='Current vs. former information' statusText={STATUS_TEXT}>
            <p>
                Mark people and company details as current or former to keep information accurate,
                reduce clutter, and manage contacts more effectively.
            </p>
        </OnboardingBody>
    );
}

function PeepRedesignSlideFive() {
    return (
        <OnboardingBody title='Testing and providing feedback!' statusText={STATUS_TEXT}>
            <p>You can easily toggle the preview of new updates right from your profile panel!</p>
            <p>
                We’d also love to hear your thoughts, so feel free to share them through the
                feedback form!
            </p>
        </OnboardingBody>
    );
}
